import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const TIER_LABELS = { basic: "Basic", standard: "Standard", premium: "Premium" };
const empty = { first_name: "", last_name: "", email: "", phone: "", street: "", zip: "", city: "", device: "", device_serial: "" };

export default function ProtectionSignup() {
  const [pricing, setPricing] = useState([]);
  const [f, setF] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);

  const available = pricing.filter((p) => p.protection_tier);
  const selectedRow = available.find((p) => p.model === f.device);
  const currentPrice = settings && selectedRow ? settings.tiers[selectedRow.protection_tier] : null;

  useEffect(() => {
    api.get("/pricing").then((r) => {
      setPricing(r.data);
      const first = r.data.find((p) => p.protection_tier);
      if (first) setF((p) => ({ ...p, device: p.device || first.model }));
    }).catch(() => {});
    api.get("/protection/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim() || !f.phone.trim() ||
        !f.street.trim() || !f.zip.trim() || !f.city.trim() || !f.device_serial.trim()) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/protection/contracts", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Abschluss fehlgeschlagen.");
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="protection-signup" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-32 border-t border-white/10 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <ShieldCheck size={28} className="text-white mb-5" />
        <h1 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-3">
          Schutzpaket abschließen
        </h1>
        <p className="text-[#aaa] text-sm mb-10">
          {settings ? `${Math.round(settings.min_term_months / 12)} Jahre Mindestlaufzeit · ${settings.repairs_per_year} Reparatur/Jahr inklusive – Preis richtet sich nach deinem Gerät` : ""}
        </p>

        <form onSubmit={submit} data-testid="protection-signup-form" className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="protection-first-name" value={f.first_name} onChange={s("first_name")} placeholder="Vorname" className={inputCls} />
            <input data-testid="protection-last-name" value={f.last_name} onChange={s("last_name")} placeholder="Nachname" className={inputCls} />
          </div>
          <input data-testid="protection-email" type="email" value={f.email} onChange={s("email")} placeholder="E-Mail" className={inputCls} />
          <input data-testid="protection-phone" value={f.phone} onChange={s("phone")} placeholder="Telefon" className={inputCls} />
          <input data-testid="protection-street" value={f.street} onChange={s("street")} placeholder="Straße & Hausnummer" className={inputCls} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="protection-zip" value={f.zip} onChange={s("zip")} placeholder="PLZ" className={inputCls} />
            <input data-testid="protection-city" value={f.city} onChange={s("city")} placeholder="Ort" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Gerät</label>
            <select data-testid="protection-device" value={f.device} onChange={s("device")} className={inputCls}>
              {available.map((p) => <option key={p.id} value={p.model} className="bg-[#0a0a0a]">{p.model}</option>)}
            </select>
            {currentPrice != null && selectedRow && (
              <p data-testid="protection-device-price" className="text-sm text-emerald-400 mt-2">
                {TIER_LABELS[selectedRow.protection_tier] || selectedRow.protection_tier}-Schutzpaket: <strong>{currentPrice.toFixed(2)} € / Monat</strong>
              </p>
            )}
            {available.length === 0 && (
              <p className="text-sm text-amber-300 mt-2">Für dein Gerät ist aktuell noch kein Schutzpaket verfügbar. Bitte kontaktiere uns direkt.</p>
            )}
          </div>
          <input data-testid="protection-device-serial" value={f.device_serial} onChange={s("device_serial")} placeholder="Seriennummer / IMEI" className={inputCls} />

          <button type="submit" disabled={submitting || available.length === 0} data-testid="protection-signup-submit"
            className="w-full inline-flex items-center justify-center rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            {submitting ? "Wird weitergeleitet…" : "Weiter zur Zahlung"}
          </button>
          <p className="text-xs text-[#666] text-center leading-relaxed">
            Du wirst zu Stripe weitergeleitet, um deine Zahlungsmethode zu hinterlegen. Die erste Abbuchung erfolgt sofort,
            danach automatisch jeden Monat. Mindestlaufzeit {settings ? Math.round(settings.min_term_months / 12) : 2} Jahre, danach monatlich kündbar.
          </p>
        </form>
      </div>
    </section>
  );
}
